import React, {
    useEffect,
    useState,
    useCallback,
} from 'react';
import { EndorsementService } from 'gw-capability-policychange';
import { SgEndorsementInfo, SgEndorsementPolicies, CepConstants } from 'saga-cep-specific-components';
import SgWebchatInfo from 'sg-webchat-info';
import SpmConstants from 'sg-spm-constants';
import SgCepSideMenu from 'sg-cep-side-menu';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { Loader } from '@jutro/components';
import _ from 'lodash';
import { publish } from '@jutro/events';
import {
    ADOBE_TOPICS,
    useSgTrackPageName,
    SgSessionStorageService
} from 'saga-modules';
import { WizardPage } from 'gw-portals-wizard-react';

import metadata from './SgEndorsementEntryPage.metadata.json5';
import './SgEndorsementEntryPage.messages';

function SgEndorsementEntryPage(props) {
    const {
        wizardData: modelVM,
        updateWizardData: updateData,
        sgWizardPropsExt,
        goNext,
        updateStepsExt
    } = props;

    const {
        handleGoBack
    } = sgWizardPropsExt;

    const [policyNumber, setPolicyNumber] = useState(_.get(modelVM, 'policyNumber.value'));
    const [isLoading, setLoadingState] = useState(true);
    const [policies, setPolicies] = useState([]);
    const [currentPolicy, setCurrentPolicy] = useState(undefined);

    useSgTrackPageName(CepConstants.PAGE_NAMES.CHANGE_POLICY);

    useEffect(() => {
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.ADDON_ORIGINALLY_SELECTED);
        EndorsementService.getAvailablePolicies()
            .then((result) => {
                setPolicies(result.filter((policy) => policy.policyHeader.currentOrRenewed !== CepConstants.RENEWED_POLICY_HEADER));
                setLoadingState(false);
            });
    }, []);

    useEffect(() => {
        if (!_.isNil(policyNumber)) {
            setCurrentPolicy(_.find(policies, (p) => p.policyNumber === policyNumber));
        } else if (policies.length === 1 && !currentPolicy) {
            setCurrentPolicy(policies[0]);
        } else if (policies.length > 1) {
            setCurrentPolicy(undefined);
        }
    }, [policies, policyNumber]);

    useEffect(() => {
        if (currentPolicy) {
            publish(ADOBE_TOPICS.FILTER_JOURNEY_PRODUCT, {
                policyNumber: currentPolicy.policyNumber
            });
        }
    }, [currentPolicy]);

    useEffect(() => {
        if (_.get(modelVM, 'effectiveDate.aspects.valid')) {
            EndorsementService.checkEffectiveDateIsValid([currentPolicy.policyNumber, modelVM.baseData.effectiveDate.value])
                .then((result) => {
                    if (!result) {
                        const error = { customErrorMessage: 'Policy change date check was not successful' };
                        throw error;
                    }
                })
                .catch((err) => {
                    const error = { error: err, customErrorMessage: 'Policy change date check was not successful' };
                    throw error;
                });
        }
    }, [_.get(modelVM, 'effectiveDate.value')]);

    const continuePolicyChange = useCallback(() => {
        updateStepsExt(modelVM);
        const addOnsCoverages = modelVM.lobData.privateMotor.offerings.children[0].coverages.vehicleCoverages.children[0].coverages;
        const isAddonOriginallySelected = {
            legalAddon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.LEGAL_PROTECTION_CODE).selected,
            keyAddon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.KEY_PROTECTION_CODE).selected,
            paAddon: addOnsCoverages.value.find((el) => el.patternCode === SpmConstants.PERSONAL_ACCIDENT_CODE).selected
        };
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.ADDON_ORIGINALLY_SELECTED, JSON.stringify(isAddonOriginallySelected));
        goNext(modelVM);
    }, [goNext, modelVM, updateStepsExt]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrideProps = {
        cepEndorsementPoliciesList: {
            policies: policies,
            setPolicyNumber: setPolicyNumber,
            visible: !currentPolicy
        },
        cepEndorsementInfo: {
            policy: currentPolicy,
            modelVM,
            updateData: updateData,
            visible: !_.isEmpty(currentPolicy)
        },
        continueChangeButton: {
            visible: _.get(modelVM, 'effectiveDate.aspects.valid'),
            disabled: !_.some(_.get(modelVM, 'lobData.privateMotor.selectedChanges.value')),
            onClick: continuePolicyChange
        },
        cepEndorsementEntryWebchatMessage: {
            showMtaMessage: true,
            visible: !_.isEmpty(currentPolicy) && !currentPolicy.disabled ? '' : false
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            handleGoBack
        },
        resolveComponentMap: {
            SgEndorsementPolicies: SgEndorsementPolicies,
            SgEndorsementInfo: SgEndorsementInfo,
            SgWebchatInfo,
            SgCepSideMenu
        }
    };

    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

export default SgEndorsementEntryPage;
